import { useEffect, useRef, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useSearchParams, Link } from "react-router-dom";
import { useCachedState } from "@/lib/swr";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useDash } from "./ctx";
import Satisfaction from "./Satisfaction";
import { SignupsReport } from "./reports/SignupsReport";
import { UsageReport } from "./reports/UsageReport";
import { ReportSchedule } from "./reports/ReportSchedule";
import { ReportTrends } from "./reports/ReportTrends";
import { resolveRange } from "./reports/shared";
import { toast } from "sonner";
import {
  Download, TrendingUp, Loader2,
  Image as ImageIcon, AlertTriangle, GraduationCap,
  Sparkles, FileText,
} from "lucide-react";
import { V } from "@/lib/vocab";

const fmtDateShort = (iso) => { try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }); } catch { return ""; } };
const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Interactive dashboard card shell.
const Card = ({ children, className = "", ...rest }) => (
  <div {...rest} className={`bg-white rounded-2xl border border-sand-200 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] ${className}`}>{children}</div>
);
const CardHead = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 px-5 pt-5 pb-3 text-[11px] font-bold tracking-[0.14em] text-ink-400 uppercase">
    {Icon && <Icon className="h-3.5 w-3.5 text-redline-500" strokeWidth={2} />} {children}
  </div>
);

export default function Reports() {
  const { tenantId, tenants, role, enterCommunity } = useDash();
  const isSuper = role === "super_admin";
  const readOnly = role === "board_member";
  // Company console (no community selected): platform-wide Satisfaction lives here as a tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const showSatTab = !tenantId && isSuper;
  const reportsTab = showSatTab ? (searchParams.get("tab") || "reports") : "reports";
  const [communityId, setCommunityId] = useState(tenantId || "");
  const [periodKey, setPeriodKey] = useState("last_30");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useCachedState(`report:${communityId || tenantId}:${periodKey}`, null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [reportType, setReportType] = useState("board");
  const [reloadKey, setReloadKey] = useState(0);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState("");
  const logoRef = useRef(null);

  useEffect(() => {
    if (tenantId) { setCommunityId(tenantId); return; }  // follow the console's community switcher
    if (communityId) return;
    if (isSuper && tenants?.length) setCommunityId(tenants[0].id);
  }, [tenantId, tenants]); // eslint-disable-line

  const activeTid = communityId || tenantId;

  const load = async () => {
    if (!activeTid) return;
    setLoading(true);
    const { start, end } = resolveRange(periodKey, customStart, customEnd);
    try {
      const { data } = await api.get(`/tenants/${activeTid}/report`, { params: { start: start.toISOString(), end: end.toISOString() } });
      setData(data);
    } catch { toast.error("Could not generate the report."); }
    finally { setLoading(false); }
  };

  // The on-screen board report renders the EXACT PDF/email HTML so screen and file match (iter452d).
  const loadPreview = async () => {
    if (!activeTid) return;
    setPreviewLoading(true); setPreviewErr("");
    try {
      const { start, end } = resolveRange(periodKey, customStart, customEnd);
      const r = await api.post(`/tenants/${activeTid}/report/preview`, { start: start.toISOString(), end: end.toISOString() });
      setPreviewHtml(r.data?.html || "");
    } catch (e) {
      setPreviewErr(e?.response?.data?.detail || "Couldn't build the report — please try again.");
    } finally { setPreviewLoading(false); }
  };

  // eslint-disable-next-line
  useEffect(() => { if (activeTid) load(); }, [activeTid, periodKey]);
  // eslint-disable-next-line
  useEffect(() => { if (activeTid && reportType === "board" && periodKey !== "custom") loadPreview(); }, [activeTid, periodKey, reportType]);

  const periods = [["last_30", "Last 30 days"], ["this_month", "This month"], ["last_month", "Last month"], ["custom", "Custom"]];

  const uploadLogo = async (file) => {
    if (!file || !activeTid) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/tenants/${activeTid}/config`, { report_logo_path: r.data.path });
      setData((d) => (d ? { ...d, community: { ...d.community, logo_path: r.data.path } } : d));
      loadPreview();
      toast.success("Logo added to report");
    } catch { toast.error("Logo upload failed"); }
    finally { setUploadingLogo(false); }
  };

  // Download the SAME rich PDF the scheduled email attaches (server-rendered: "At a glance"
  // sentiment gauge, resolution split, satisfaction thumbs, inbound chart) so the Reports-tab
  // download and the email are identical — one source of truth, one hours-saved figure.
  const exportPdf = async () => {
    if (!activeTid || exporting) return;
    setExporting(true);
    try {
      const { start, end } = resolveRange(periodKey, customStart, customEnd);
      const res = await api.post(
        `/tenants/${activeTid}/report/download`,
        { start: start.toISOString(), end: end.toISOString() },
        { responseType: "blob" },
      );
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const fname = m ? decodeURIComponent(m[1].trim())
        : `${(data?.community?.name || "Community").replace(/[^\w\s-]/g, "")} - Concierge Report.pdf`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Export failed — please try again");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="reports-page" className="pb-10 space-y-6">
      {showSatTab && (
        <div className="inline-flex rounded-full bg-sand-100 border border-sand-200 p-1" data-testid="reports-tabs">
          {[["reports", `${V.Grp()} reports`], ["satisfaction", "Satisfaction"]].map(([k, l]) => (
            <button key={k} data-testid={`reports-tab-${k}`} onClick={() => setSearchParams(k === "reports" ? {} : { tab: k })}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${reportsTab === k ? "bg-ink text-white shadow-sm" : "text-ink/60 hover:text-ink"}`}>
              {l}
            </button>
          ))}
        </div>
      )}
      {reportsTab === "satisfaction" ? <Satisfaction /> : (<>
      {/* Report type switcher */}
      <div className="inline-flex rounded-full bg-sand-100 border border-sand-200 p-1" data-testid="report-type-switcher">
        {[["board", `${V.Grp()} report`], ["signups", "New sign-ups"], ["usage", "Member usage"], ["schedule", "Schedule & email"]].map(([k, l]) => (
          <button key={k} data-testid={`report-type-${k}`} onClick={() => setReportType(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${reportType === k ? "bg-ink text-white shadow-sm" : "text-ink/60 hover:text-ink"}`}>
            {l}
          </button>
        ))}
      </div>
      {/* Controls */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div>
          <PageTitle title={<>{reportType === "signups" ? "New sign-ups" : reportType === "usage" ? "Member usage" : reportType === "schedule" ? `${V.one("org")} report schedule` : `${V.Grp()} report`}</>} about={<>{reportType === "signups"
              ? "Everyone who joined the roster in this period — how they joined and where their approval stands."
              : reportType === "usage"
                ? "Who's actually using the concierge — adoption, top users, first-timers and channels."
                : reportType === "schedule"
                  ? `Have this ${V.lc("org")}'s ${V.grp()} report emailed to your whole management team and ${V.grp()} automatically, on a shared schedule — no logging in required. (To manage your own personal alerts, use Notifications settings.)`
                  : `A live summary of everything your concierge handled — including trends over time. Explore it here, then export a crisp, ${V.grp()}-ready PDF.`}</>} />
        </div>
        <div className="flex flex-wrap items-center gap-3" data-testid="report-date-selector">
          {isSuper && (
            <select data-testid="report-community-select" value={communityId}
              // Inside a community the whole console switches (branding, menu AND data move together);
              // on the company console it only picks which report to build.
              onChange={(e) => (tenantId ? enterCommunity(e.target.value, "/dashboard/reports") : setCommunityId(e.target.value))}
              className="rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-redline-300 shadow-sm">
              {(tenants || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {reportType !== "schedule" && (
            <div className="inline-flex rounded-full bg-sand-100 border border-sand-200 p-1">
              {periods.map(([k, l]) => (
                <button key={k} data-testid={`report-period-${k}`} onClick={() => setPeriodKey(k)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${periodKey === k ? "bg-ink text-white shadow-sm" : "text-ink-600 hover:text-ink"}`}>{l}</button>
              ))}
            </div>
          )}
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
          {reportType === "board" && !readOnly && (
            <button data-testid="report-logo-btn" onClick={() => logoRef.current?.click()} disabled={uploadingLogo}
              className="shrink-0 rounded-full border border-sand-200 bg-white text-ink-700 font-semibold px-4 py-2.5 flex items-center gap-2 hover:border-redline-300 transition-colors disabled:opacity-50 shadow-sm">
              {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} {data?.community?.logo_path ? "Change logo" : "Add logo"}
            </button>
          )}
          {reportType === "board" && (
            <button data-testid="export-pdf-button" onClick={exportPdf} disabled={!data || loading || exporting}
              className="shrink-0 rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors disabled:opacity-50 shadow-sm">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {exporting ? "Preparing…" : "Download PDF"}
            </button>
          )}
        </div>
      </div>

      {periodKey === "custom" && (
        <div className="flex items-center gap-2 text-sm -mt-2">
          <input type="date" data-testid="report-custom-start" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="rounded-lg border border-sand-200 px-3 py-1.5 outline-none focus:border-redline-300" />
          <span className="text-ink-500">to</span>
          <input type="date" data-testid="report-custom-end" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="rounded-lg border border-sand-200 px-3 py-1.5 outline-none focus:border-redline-300" />
          <button data-testid="report-generate-btn" onClick={() => { if (reportType === "board") { load(); loadPreview(); } setReloadKey((k) => k + 1); }} className="rounded-full bg-redline-500 text-white font-semibold px-4 py-1.5 hover:bg-redline-600 transition-colors">Generate</button>
        </div>
      )}

      {reportType === "signups" && (
        <SignupsReport tenantId={activeTid} periodKey={periodKey} customStart={customStart} customEnd={customEnd} reloadKey={reloadKey} />
      )}
      {reportType === "usage" && (
        <UsageReport tenantId={activeTid} periodKey={periodKey} customStart={customStart} customEnd={customEnd} reloadKey={reloadKey} />
      )}
      {reportType === "schedule" && (
        <ReportSchedule tenantId={activeTid} readOnly={readOnly} />
      )}

      {reportType === "board" && loading && !data && <div className="flex items-center gap-2 text-ink-500 py-24 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Building report…</div>}

      {reportType === "board" && (
        <div data-testid="board-report" className="space-y-6">
          {/* The exact report you download / email — rendered live for the selected range */}
          <div className="rounded-2xl border border-sand-200 bg-[#f3f0ea] overflow-hidden shadow-sm" data-testid="board-report-document">
            {previewLoading && (
              <div data-testid="board-report-loading" className="flex items-center justify-center gap-2 text-ink-500 py-24"><Loader2 className="h-5 w-5 animate-spin" /> Building report…</div>
            )}
            {!previewLoading && previewErr && (
              <div data-testid="board-report-error" className="py-24 text-center text-sm text-redline-600 px-6">{previewErr}</div>
            )}
            {!previewLoading && !previewErr && previewHtml && (
              <iframe data-testid="board-report-frame" title="Concierge report" sandbox="" srcDoc={previewHtml}
                className="w-full border-0 bg-[#f3f0ea]" style={{ height: "min(80vh, 1200px)" }} />
            )}
          </div>

          {data && (<>
          {/* Interactive tools — not part of the static PDF, so they live below the document */}
          <div className="flex items-center gap-3 pt-2" data-testid="board-interactive-divider">
            <span className="h-px flex-1 bg-sand-200" />
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink-400 whitespace-nowrap">Interactive tools</span>
            <span className="h-px flex-1 bg-sand-200" />
          </div>

          {/* Self-learning: what the AI couldn't answer + teach-it-once loop */}
          <Card data-testid="knowledge-gaps-card">
            <CardHead icon={GraduationCap}>What your AI couldn&apos;t answer</CardHead>
            <div className="px-5 pb-5">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs font-semibold rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1" data-testid="gaps-open-chip">
                  {data.knowledge?.gaps_open ?? 0} question{(data.knowledge?.gaps_open ?? 0) === 1 ? "" : "s"} waiting for an answer
                </span>
                <span className="text-xs font-semibold rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1" data-testid="gaps-resolved-chip">
                  {data.knowledge?.gaps_resolved ?? 0} taught to the AI this period
                </span>
              </div>
              {(data.knowledge?.top_unanswered || []).length === 0 ? (
                <p className="text-sm text-ink-500">Nothing waiting — your AI has an answer for everything {V.lcs("person")} have asked.</p>
              ) : (
                <div className="space-y-2">
                  {data.knowledge.top_unanswered.map((g) => (
                    <div key={g.id} data-testid={`gap-row-${g.id}`} className="flex items-center gap-3 text-sm border-b border-sand-100 pb-2 last:border-0">
                      <span className="flex-1 text-ink-800 truncate">&ldquo;{g.question}&rdquo;</span>
                      {g.count > 1 && <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">asked {g.count}×</span>}
                      {g.askers > 0 && <span className="text-[11px] font-semibold text-ink-400 shrink-0">{g.askers} waiting</span>}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-ink-400 mt-4 leading-relaxed">
                Answer one of these once and your AI knows it forever — {V.lcs("person")} who asked get texted the answer automatically.{" "}
                <Link to="/dashboard/documents?improve=1" data-testid="teach-now-link" className="font-semibold text-redline-600 hover:text-redline-700 underline underline-offset-2">Teach your AI now</Link>
              </p>
            </div>
          </Card>

          {/* What your assistant learned this period (positive counterpart to the gaps above) */}
          <Card data-testid="knowledge-learned-card">
            <CardHead icon={Sparkles}>What your assistant learned</CardHead>
            <div className="px-5 pb-5">
              {(data.learned?.total ?? 0) === 0 ? (
                <p className="text-sm text-ink-500">No new documents or answers were added this period. Teaching it from any unanswered question above makes it smarter for next time.</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="text-xs font-semibold rounded-full bg-sky-50 border border-sky-200 text-sky-700 px-3 py-1" data-testid="docs-added-chip">
                      {data.learned.documents_added} document{data.learned.documents_added === 1 ? "" : "s"} indexed
                    </span>
                    <span className="text-xs font-semibold rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1" data-testid="answers-taught-chip">
                      {data.learned.answers_taught} answer{data.learned.answers_taught === 1 ? "" : "s"} taught
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(data.learned.documents || []).map((d, i) => (
                      <div key={`d${i}`} data-testid={`learned-doc-${i}`} className="flex items-center gap-3 text-sm border-b border-sand-100 pb-2 last:border-0">
                        <FileText className="h-4 w-4 text-sky-500 shrink-0" strokeWidth={2} />
                        <span className="flex-1 text-ink-800 truncate">{d.title}</span>
                        <span className="text-[11px] font-semibold text-ink-400 shrink-0">{titleCase(d.doc_type)}</span>
                      </div>
                    ))}
                    {(data.learned.answers || []).map((a, i) => (
                      <div key={`a${i}`} data-testid={`learned-answer-${i}`} className="flex items-center gap-3 text-sm border-b border-sand-100 pb-2 last:border-0">
                        <GraduationCap className="h-4 w-4 text-emerald-500 shrink-0" strokeWidth={2} />
                        <span className="flex-1 text-ink-800 truncate">{a.title}</span>
                        {a.from_ticket && <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 shrink-0">from a ticket</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-ink-400 mt-4 leading-relaxed">Everything added here is now part of your assistant’s permanent memory — it answers {V.lcs("person")} with it automatically, and it stays when your {V.grp()} or manager changes.</p>
                </>
              )}
            </div>
          </Card>

          {/* Notable escalations */}
          <Card data-testid="notable-escalations">
            <CardHead icon={AlertTriangle}>Notable escalations</CardHead>
            <div className="px-5 pb-5">
              {(data.escalations || []).length === 0 ? (
                <p className="text-sm text-ink-500">Nothing needed a person this period — the concierge handled everything on its own.</p>
              ) : (
                <div>
                  {data.escalations.map((e, i) => (
                    <div key={i} data-testid={`escalation-row-${i}`} className="flex flex-col md:flex-row md:items-start gap-2 md:gap-5 py-4 border-b border-sand-100 last:border-0">
                      <div className="md:w-44 shrink-0">
                        <div className="font-semibold text-ink text-sm">{e.name}</div>
                        <div className="text-xs text-ink-400 mt-0.5">{homeLabel(e) ? `${homeLabel(e)} · ` : ""}{fmtDateShort(e.at)}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="inline-block text-[11px] font-semibold rounded-full bg-sand-100 text-ink-600 px-2.5 py-0.5 mb-1.5">{e.reason}</span>
                        <p className="text-sm text-ink-800 leading-relaxed">{e.question ? `"${e.question}"` : <span className="text-ink-400 italic">No message captured</span>}</p>
                      </div>
                      <div className="md:w-24 shrink-0 md:text-right">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${e.status === "resolved" ? "text-emerald-600" : "text-amber-600"}`}>
                          <span className={`h-2 w-2 rounded-full ${e.status === "resolved" ? "bg-emerald-500" : "bg-amber-500"}`} /> {e.status === "resolved" ? "Resolved" : "Open"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          <div data-testid="board-report-trends" className="space-y-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-redline-500" />
              <h2 className="font-heading text-xl font-bold text-ink">Trends over time</h2>
            </div>
            <ReportTrends tenantId={activeTid} />
          </div>
          </>)}
        </div>
      )}
      </>)}
    </div>
  );
}
